import { logger } from '@/utils/logger';
import { useAuthActions } from '@convex-dev/auth/react';
import { useConvexAuth, useQuery } from 'convex/react';
import { useEffect, useState } from 'react';
import { api } from '../../../../convex/_generated/api';

/**
 * Extract stable user ID from Convex Auth tokenIdentifier
 * @param tokenIdentifier - The tokenIdentifier from Convex Auth
 * @returns Stable user ID that works across sessions
 */
function extractUserId(tokenIdentifier: string): string {
  const parts = tokenIdentifier.split('|');
  if (parts.length < 2) {
    logger.warn('Invalid tokenIdentifier format:', tokenIdentifier);
    return tokenIdentifier; // Fallback to full identifier
  }
  return parts[1]; // Return the stable user ID part
}

/**
 * Simplified auth hook that provides the same interface as the old Supabase useAuth
 * but uses Convex Auth underneath with stable user identification across sessions
 */
export const useAuth = () => {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const { signIn, signOut } = useAuthActions();
  const [showAccountLinking, setShowAccountLinking] = useState(false);

  // Get the current user from the database
  const userIdentity = useQuery(api.users.getCurrentUser);

  // Check for account linking suggestions
  const linkingSuggestions = useQuery(api.accountLinking.getAccountLinkingSuggestions);

  // Combine loading states - we're loading if either auth is loading or user query is loading
  const loading = isLoading || (isAuthenticated && userIdentity === undefined);

  // Create a normalized user object that matches expected interface
  const user = userIdentity
    ? (() => {
        try {
          return {
            // Use stable part of tokenIdentifier (works across sessions and linked OAuth accounts)
            // tokenIdentifier format: "issuerUrl|userId|sessionSuffix" - we want the userId part (index 1)
            id: extractUserId(userIdentity.tokenIdentifier),
            // For backward compatibility, also provide subject field
            subject: extractUserId(userIdentity.tokenIdentifier),
            email: userIdentity.email || userIdentity.emailVerified || 'user@example.com',
            name: userIdentity.name || userIdentity.givenName || userIdentity.nickname || 'User',
            picture:
              (userIdentity as any).picture ||
              (userIdentity as any).pictureUrl ||
              (userIdentity as any).image,
            // Include raw identity for components that need it
            identity: userIdentity,
            // Legacy compatibility
            user_metadata: {
              full_name: userIdentity.name || userIdentity.givenName || userIdentity.nickname,
              avatar_url:
                (userIdentity as any).picture ||
                (userIdentity as any).pictureUrl ||
                (userIdentity as any).image,
            },
          };
        } catch (error) {
          logger.errorWithException('Error creating user object:', error);
          return null;
        }
      })()
    : null;

  // Handle account linking detection
  useEffect(() => {
    if (isAuthenticated && user && linkingSuggestions?.canLink) {
      // Show account linking modal if there are linkable accounts
      setShowAccountLinking(true);
    }
  }, [isAuthenticated, user, linkingSuggestions?.canLink]);

  // Handle post-OAuth redirect cleanup
  useEffect(() => {
    if (isAuthenticated && user && typeof window !== 'undefined') {
      // Check if we have a stored redirect URL from OAuth flow
      const storedRedirect = localStorage.getItem('oauth_redirect_url');
      if (storedRedirect && storedRedirect !== window.location.href) {
        localStorage.removeItem('oauth_redirect_url');
        // Only redirect if we're currently on a login-related page
        const currentPath = window.location.pathname;
        if (currentPath === '/login' || currentPath === '/app/auth') {
          window.location.href = storedRedirect;
        }
      }
    }
  }, [isAuthenticated, user]);

  // Auth method implementations using Convex Auth
  const signInWithOTP = async (email: string, code?: string) => {
    if (code) {
      // Step 2: Verify the OTP code
      await signIn('resend-otp', { email, code });
    } else {
      // Step 1: Send OTP code
      await signIn('resend-otp', { email });
    }
  };

  const signInWithGoogle = async () => {
    // Store the intended destination URL before OAuth redirect

    const getRedirectUrl = () => {
      const currentPath = window.location.pathname;
      const isLoginPage = currentPath === '/login' || currentPath === '/app/auth';

      const redirectUrl = isLoginPage ? '/app' : window.location.href;
      localStorage.setItem('oauth_redirect_url', redirectUrl);

      return redirectUrl;
    };

    await signIn('google', {
      redirectTo: getRedirectUrl(),
    });
  };

  const signInWithFacebook = async () => {
    // Store the intended destination URL before OAuth redirect
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      const isLoginPage = currentPath === '/login' || currentPath === '/app/auth';

      // If we're on a login page, redirect to app home, otherwise stay on current page
      const redirectUrl = isLoginPage ? '/app' : window.location.href;
      localStorage.setItem('oauth_redirect_url', redirectUrl);
    }

    await signIn('facebook');
  };

  const handleSignOut = async () => {
    // Clear any stored OAuth redirect URLs on sign out
    if (typeof window !== 'undefined') {
      localStorage.removeItem('oauth_redirect_url');
    }
    await signOut();
  };

  const handleAccountLinkingSuccess = () => {
    setShowAccountLinking(false);
    // Optionally refresh the page or update state
    window.location.reload();
  };

  const handleAccountLinkingClose = () => {
    setShowAccountLinking(false);
  };

  return {
    // Auth state - improved logic to handle timing issues
    isAuthenticated: isAuthenticated && user !== null,
    loading,

    // User data from Convex database - normalized format
    user,

    // Account linking state
    showAccountLinking,
    linkingSuggestions,
    handleAccountLinkingSuccess,
    handleAccountLinkingClose,

    // Auth methods - OTP and OAuth only
    signInWithOTP,
    signInWithGoogle,
    signInWithFacebook,
    signOut: handleSignOut,

    // Also provide direct access to signIn and signOut for flexibility
    signIn,

    // Session compatibility - provide user data as session for legacy components
    session: user ? { user } : null,
  };
};
